import { SkeletonText, Text, Tooltip } from '@customerio/pluma-components/react';
import { formatNumber, formatNumberAndPluralize } from '@/utils/number';
import { shareOf } from '../utils/share';

/**
 * The share of people or objects that carry a row, as a percentage with the
 * counts behind it on hover.
 *
 * A share that rounds away to nothing but is not nothing reads as "<1%", so
 * a rare attribute is not indistinguishable from an unused one — `shareOf`
 * owns that rule, because the item page states the same share in a sentence
 * and the two used to disagree between a half percent and one. An unknown count
 * shows an em dash: the backend answers profile counts within a budget and
 * omits what it could not reach, and a missing count is not a count of zero.
 *
 * A count still being read is a third state, and a skeleton rather than that
 * dash — otherwise every row claims the backend gave up on it until the read
 * lands.
 */

export type PercentageCellProps = {
  /** How many carry the row, or undefined while the count is unknown. */
  matching: number | undefined;
  total: number;
  /** The noun the tooltip counts in, singular. */
  noun: string;
  /** What the tooltip says they did with the row. */
  verb: string;
  isLoading: boolean;
};

export function PercentageCell({ matching, total, noun, verb, isLoading }: PercentageCellProps) {
  if (isLoading) {
    return <SkeletonText text="product-p" lines={['40px']} />;
  }
  if (matching == null || total === 0) {
    return <Text>—</Text>;
  }
  if (matching === 0) {
    return <Text>{shareOf(0, total)}</Text>;
  }

  return (
    <Tooltip content={`${formatNumber(matching)} of ${formatNumberAndPluralize(total, noun)} ${verb}`} isUnderlined>
      <Text>{shareOf(matching, total)}</Text>
    </Tooltip>
  );
}
